import { useState } from "react";
import axios from "axios";
import { PageHero } from "@/components/Section";
import { COMPANY, ROLES, NEEDS, COUNTRIES, IMAGES, whatsappLink } from "@/data/site";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle2, Zap } from "lucide-react";
import { trackEnquirySubmit, trackWhatsAppClick, trackPhoneClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "", company_name: "", email: "", phone: "",
  role: "Contractor", need: "Hardware Supply",
  country: "", message: "", source_page: "contact",
};

const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ loading: false, ok: null, msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: null, msg: "" });
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      trackEnquirySubmit({ role: form.role, need: form.need, country: form.country || "unspecified" });
      setStatus({ loading: false, ok: true, msg: data.message || "Enquiry received." });
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Please call or WhatsApp us directly.";
      setStatus({ loading: false, ok: false, msg });
    }
  };

  return (
    <div className="bg-white p-7 md:p-10 border-l-[3px] border-[#E8721C]">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-display text-[30px] md:text-[40px] leading-[1] heading-bar mb-0">TELL US WHAT YOU NEED</h2>
      </div>
      <div className="inline-flex items-center gap-2 bg-[#E8721C]/10 text-[#0A1628] px-3 py-2 text-[12px] font-bold tracking-[.14em] mb-6">
        <Zap size={14} className="text-[#E8721C]"/> AVERAGE RESPONSE: UNDER 4 BUSINESS HOURS
      </div>

      {status.ok && (
        <div className="mb-6 p-5 border-l-[3px] border-[#25D366] bg-[#25D366]/10 flex items-start gap-3" data-testid="form-success">
          <CheckCircle2 className="text-[#25D366] mt-0.5" size={22}/>
          <div><div className="font-bold">Enquiry received.</div><div className="text-sm text-[#1E2D40]">{status.msg}</div></div>
        </div>
      )}
      {status.ok === false && (
        <div className="mb-6 p-5 border-l-[3px] border-red-500 bg-red-50 text-red-700" data-testid="form-error">{status.msg}</div>
      )}

      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="enquiry-form">
        <div><label className="cg-label">Full Name *</label><input required minLength={2} value={form.full_name} onChange={update("full_name")} className="cg-input" data-testid="input-full-name"/></div>
        <div><label className="cg-label">Company Name *</label><input required value={form.company_name} onChange={update("company_name")} className="cg-input" data-testid="input-company-name"/></div>
        <div><label className="cg-label">Email *</label><input required type="email" value={form.email} onChange={update("email")} className="cg-input" data-testid="input-email"/></div>
        <div><label className="cg-label">Phone *</label><input required value={form.phone} onChange={update("phone")} className="cg-input" data-testid="input-phone"/></div>
        <div><label className="cg-label">I am a</label>
          <select className="cg-select" value={form.role} onChange={update("role")} data-testid="select-role">
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div><label className="cg-label">I need</label>
          <select className="cg-select" value={form.need} onChange={update("need")} data-testid="select-need">
            {NEEDS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><label className="cg-label">Country (for export clients)</label>
          <select className="cg-select" value={form.country} onChange={update("country")} data-testid="select-country">
            <option value="">— Select your country —</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><label className="cg-label">Message / Project Details *</label>
          <textarea required minLength={3} value={form.message} onChange={update("message")} className="cg-textarea" placeholder="Tell us about your project — product types, quantities, timeline, location…" data-testid="input-message"/>
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={status.loading} className="btn-orange w-full justify-center disabled:opacity-60" data-testid="submit-btn">
            <Send size={16}/> {status.loading ? "SENDING…" : "SEND MY ENQUIRY — WE REPLY IN 24 HRS"}
          </button>
        </div>
      </form>
      <p className="mt-4 text-[13px] text-[#1E2D40]/70">We respond to all enquiries within 24 business hours. For urgent orders, please call or WhatsApp directly.</p>
    </div>
  );
};

const ContactInfoPanel = () => (
  <>
    <div className="bg-[#0A1628] text-white p-7 md:p-10 corner-accent" data-testid="contact-info">
      <div className="text-[12px] tracking-[.22em] text-[#E8721C] font-bold mb-3">DIRECT CONTACT</div>
      <h3 className="font-display text-[28px] md:text-[34px] heading-bar leading-tight">SPEAK TO THE TEAM.</h3>
      <div className="mt-2 mb-6 text-[#8A96A3] text-[14px]">{COMPANY.director} · Director</div>
      <ul className="space-y-4">
        {COMPANY.phones.map((p) => (
          <li key={p}><a onClick={() => trackPhoneClick("contact-info")} href={`tel:${p.replace(/\s/g,'')}`} className="flex items-center gap-3 hover:text-[#E8721C]"><Phone size={18} className="text-[#E8721C]"/><span>{p}</span></a></li>
        ))}
        <li><a onClick={() => trackWhatsAppClick("contact-info")} href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#25D366]"><MessageCircle size={18}/> WhatsApp Us</a></li>
        {COMPANY.emails.map((m) => (
          <li key={m}><a href={`mailto:${m}`} className="flex items-center gap-3 hover:text-[#E8721C]"><Mail size={18} className="text-[#E8721C]"/>{m}</a></li>
        ))}
        <li className="flex items-start gap-3"><MapPin size={18} className="text-[#E8721C] mt-0.5"/><span>{COMPANY.address}<br/><span className="text-[#8A96A3] text-sm">{COMPANY.poBox}</span></span></li>
        <li className="flex items-start gap-3"><Clock size={18} className="text-[#E8721C] mt-0.5"/><span>{COMPANY.hours.map((h)=> <span key={h} className="block">{h}</span>)}</span></li>
      </ul>
      <div className="mt-6 pt-6 border-t border-white/10 text-[12px] text-[#8A96A3] tracking-[.16em]">REGISTRATION · {COMPANY.reg}</div>
    </div>
    <div className="mt-6 border-l-[3px] border-[#E8721C] bg-white">
      <iframe
        title="Cassie Gee location"
        src="https://www.google.com/maps?q=5+Myrtle+Street+Clydesdale+Pretoria&output=embed"
        width="100%" height="280" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  </>
);

const Contact = () => (
  <>
    <PageHero
      label="GET IN TOUCH"
      title="LET'S START SOMETHING."
      subtitle="Call us. WhatsApp us. Fill in the form. However you prefer — we'll respond within 24 hours."
      image={IMAGES.pretoria}
    />
    <section className="bg-[#F8F6F2] py-20" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7"><EnquiryForm /></div>
        <div className="lg:col-span-5"><ContactInfoPanel /></div>
      </div>
    </section>
  </>
);
export default Contact;
